import { Button, Divider, Header, Label, Modal, Select, Text } from '@opengov/component-library/dist';
import { FilterableInput, IReactSelectOption } from '@opengov/component-library/dist/FilterableInput/FilterableInput';
import { ILocalized } from '../../../../../src/locale/types';
import { EGranteeType, EReportActionSet, IGrantee, IInvitableUser } from '@opengov/reporting-types';
import { List, Map } from 'immutable';
import React, { useState } from 'react';
import { ReportAccessGrid } from '../../../../reportSettings/ReportSettings/ReportAccessGrid/ReportAccessGrid';
import { granteeCellRendererSelector } from '../../../../reportSettings/ReportSettings/ReportGranteeCellRenderer';
import { IReportLocale } from '../../../locale';
import styles from './ManageAccessModal.scss';

export interface IManageAccessModalProps extends ILocalized {
  reportTitle: string;
  currentUserId: string;
  currentGrantees: Map<string, IGrantee>;
  entityId: number;
  temporaryGrantees: Map<string, IGrantee>;
  invitableUsers: List<IInvitableUser>;
  saveAccess(updatedGrantees: Map<string, IGrantee>): void;
  updateTemporaryGrantee(grantee: IGrantee): void;
  closeModal(): void;
  isReportMultiDataset: boolean;
}

const filterConfig = {
  stringify: (option) => `${option.label} ${option.data.meta}`,
  ignoreAccents: false
};

export const handleAddGrantees = (
  newGrantees: List<IReactSelectOption>,
  newUserPermission: EReportActionSet,
  setCurrentUsersToAdd: (users: List<IReactSelectOption>) => void,
  updateTemporaryGrantee: (grantee: IGrantee) => void
) => {
  // update grantees in state
  newGrantees.forEach((user) => {
    updateTemporaryGrantee({
      granteeId: user.value,
      granteeName: user.label,
      granteeType: EGranteeType.User,
      actionSet: newUserPermission,
      granteeEmail: user.meta ?? ''
    });
  });

  setCurrentUsersToAdd(List<IReactSelectOption>([]));
};

export const handleSelectGrantee = (
  addedUsers: IReactSelectOption[],
  setCurrentUsersToAdd: (users: List<IReactSelectOption>) => void
) => {
  setCurrentUsersToAdd(List(addedUsers));
};

type SelectOption = { label: string; value: EReportActionSet | 'none' };
export const accessCellRendererSelector = (
  params,
  currentUserId: string,
  entityId: number,
  reportLocaleText: IReportLocale,
  isReportMultiDataset: boolean
) => {
  const newParams = { disabled: false, selectOptions: [] as SelectOption[] };
  if (params.data.granteeId === 'entity_admin' || params.data.granteeId === currentUserId) {
    newParams.disabled = true;
  }
  if (params.data.granteeId === `/entity/${entityId}`) {
    newParams.selectOptions = [
      { label: reportLocaleText.manageAccess.noAccess, value: EReportActionSet.ReportNoAccess },
      { label: reportLocaleText.manageAccess.viewer, value: EReportActionSet.ReportViewer }
    ];
  } else {
    newParams.selectOptions = [
      { label: reportLocaleText.manageAccess.viewer, value: EReportActionSet.ReportViewer },
      {
        label: isReportMultiDataset
          ? reportLocaleText.manageAccess.savedViewEditor
          : reportLocaleText.manageAccess.editor,
        value: isReportMultiDataset ? EReportActionSet.ReportSavedViewEditor : EReportActionSet.ReportEditor
      },
      { label: reportLocaleText.manageAccess.owner, value: EReportActionSet.ReportOwner },
      { label: reportLocaleText.manageAccess.remove, value: 'none' }
    ];
  }

  return {
    component: 'SelectCellRenderer',
    params: newParams
  };
};

export const changeAccessHandler = (
  newActionSet: EReportActionSet,
  granteeRow: IGrantee,
  updateTemporaryGrantee: (grantee: IGrantee) => void
) => {
  const newGrantee = {
    id: granteeRow.id,
    granteeId: granteeRow.granteeId,
    granteeName: granteeRow.granteeName,
    granteeEmail: granteeRow.granteeEmail,
    granteeType: granteeRow.granteeType,
    actionSet: newActionSet
  };
  updateTemporaryGrantee(newGrantee);
};

export const handleSaveGrantees = (
  saveAccess: (grantees: Map<string, IGrantee>) => void,
  closeModal,
  temporaryGrantees
) => {
  saveAccess(temporaryGrantees);
  closeModal();
};

const getColumnDefs = (
  reportLocaleText: IReportLocale,
  currentUserId: string,
  entityId: number,
  updateTemporaryGrantee: (grantee: IGrantee) => void,
  isReportMultiDataset: boolean
) => {
  return [
    {
      headerName: reportLocaleText.manageAccess.name,
      field: 'granteeName',
      sort: 'asc' as const,
      cellRendererSelector: granteeCellRendererSelector
    },
    {
      headerName: reportLocaleText.manageAccess.email,
      field: 'granteeEmail',
      valueFormatter: ({ value }) => {
        return value ? value : '-';
      },
      width: 280 // an ad hoc number
    },
    {
      headerName: reportLocaleText.manageAccess.access,
      field: 'actionSet',
      cellRendererSelector: (params) =>
        accessCellRendererSelector(params, currentUserId, entityId, reportLocaleText, isReportMultiDataset),
      cellRendererParams: {
        ariaLabel: 'User permission',
        onChange: (value: EReportActionSet, data: IGrantee) => {
          changeAccessHandler(value, data, updateTemporaryGrantee);
        }
      }
    },
    {
      field: 'granteeId',
      hide: true
    },
    {
      field: 'granteeType',
      hide: true // only used to conditionally render styles in 'name' column
    },
    {
      field: 'id',
      hide: true
    }
  ];
};

export const ManageAccessModal = (props: IManageAccessModalProps) => {
  const {
    reportTitle,
    reportLocaleText,
    currentGrantees,
    temporaryGrantees,
    invitableUsers,
    currentUserId,
    entityId,
    closeModal,
    saveAccess,
    updateTemporaryGrantee,
    isReportMultiDataset
  } = props;

  const [currentUsersToAdd, setCurrentUsersToAdd] = useState(List<IReactSelectOption>([]));
  const [newUserPermission, setNewUserPermission] = useState(EReportActionSet.ReportViewer);

  const changesMade = !currentGrantees.equals(temporaryGrantees);

  const currentInvitableUsers = invitableUsers
    .map((user) => {
      const isAdded = !!temporaryGrantees.find((grantee) => grantee.granteeId === user.granteeId);
      return {
        label: user.granteeName,
        meta: user.granteeEmail,
        value: user.granteeId,
        isDisabled: isAdded || currentUserId === user.granteeId,
        secondaryLabel: isAdded ? 'Added' : ''
      };
    })
    .toArray();

  return (
    <Modal width="wide" onClose={closeModal}>
      <div className={styles.header}>
        <Label>{reportTitle}</Label>
        <Header variant={'h2'}>{reportLocaleText.manageAccess.manageAccess}</Header>
      </div>

      <div className={styles.filterableInput}>
        <Label htmlFor="manage-access-add-users-input"> {reportLocaleText.manageAccess.addUsers}</Label>
        <FilterableInput
          inputId="manage-access-add-users-input"
          value={currentUsersToAdd.toArray()}
          variant="multi"
          placeholder=""
          options={currentInvitableUsers}
          emptyHeader={reportLocaleText.manageAccess.notFound}
          emptyBody={reportLocaleText.manageAccess.emptySearch}
          filterConfig={filterConfig}
          onChange={(value: IReactSelectOption[]) => {
            handleSelectGrantee(value, setCurrentUsersToAdd);
          }}
          dataTest="user-search-filterable-input"
        />
      </div>

      <div className={styles.buttons}>
        <Select
          value={newUserPermission}
          onChange={setNewUserPermission}
          dataTest="new-user-permission-select"
          buttonAriaLabel={reportLocaleText.manageAccess.access}
        >
          <Select.Option
            dataTest="new-user-permission-option-viewer"
            label={reportLocaleText.manageAccess.viewer}
            value={EReportActionSet.ReportViewer}
          />
          <Select.Option
            dataTest="new-user-permission-option-editor"
            label={
              isReportMultiDataset
                ? reportLocaleText.manageAccess.savedViewEditor
                : reportLocaleText.manageAccess.editor
            }
            value={isReportMultiDataset ? EReportActionSet.ReportSavedViewEditor : EReportActionSet.ReportEditor}
          />
          <Select.Option
            dataTest="new-user-permission-option-owner"
            label={reportLocaleText.manageAccess.owner}
            value={EReportActionSet.ReportOwner}
          />
        </Select>
        <Button
          dataTest="add-user-button"
          disabled={currentUsersToAdd.size < 1}
          variant={'neutral'}
          onClick={() => {
            handleAddGrantees(currentUsersToAdd, newUserPermission, setCurrentUsersToAdd, updateTemporaryGrantee);
          }}
        >
          {reportLocaleText.manageAccess.add}
        </Button>
      </div>
      <Divider.Horizontal />
      <Header className={styles.gridTitle} variant={'h3'}>
        {reportLocaleText.manageAccess.usersAndGroups}
      </Header>
      <div className={styles.gridContainer}>
        <ReportAccessGrid
          granteeRows={temporaryGrantees}
          columnDefs={getColumnDefs(
            reportLocaleText,
            currentUserId,
            entityId,
            updateTemporaryGrantee,
            isReportMultiDataset
          )}
        />
      </div>
      <div className={styles.changesMadeWrapper}>
        {changesMade && (
          <Text font={'condensed'} size={'small'} color={'light'} dataTest="changes-made-text">
            {reportLocaleText.manageAccess.changesMade}
          </Text>
        )}
      </div>
      <div className={styles.buttons}>
        <Button variant={'neutral'} onClick={closeModal} dataTest="cancel-manage-acess-button">
          {reportLocaleText.manageAccess.cancel}
        </Button>

        <Button
          variant={'primary'}
          disabled={!changesMade}
          onClick={() => {
            handleSaveGrantees(saveAccess, closeModal, temporaryGrantees);
          }}
          dataTest="save-manage-acess-button"
        >
          {reportLocaleText.manageAccess.save}
        </Button>
      </div>
    </Modal>
  );
};
